import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Check, X, Loader } from 'lucide-react';
import api from '../api/api';
import useAuthStore from '../store/authStore';
import '../styles/payment.css';

export default function PaymentPage() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [status, setStatus] = useState('loading');
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const verifyPayment = async () => {
      try {
        const ref = searchParams.get('ref');

        if (ref) {
          // Verify with reference (callback from Santim Pay)
          const verifyRes = await api.get(`/payment/verify/${ref}`);

          if (verifyRes.data.success) {
            setStatus('success');
            setBooking(verifyRes.data);
          } else {
            setStatus('pending');
            setError('Payment verification pending. Please check back shortly.');
          }
        } else {
          // Get payment status
          const statusRes = await api.get(`/payment/status/${bookingId}`);

          if (statusRes.data.status === 'confirmed') {
            setStatus('success');
          } else if (statusRes.data.status === 'pending') {
            setStatus('pending');
          } else {
            setStatus('failed');
          }

          setBooking(statusRes.data);
        }
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.message || 'Error verifying payment');
      }
    };

    verifyPayment();
  }, [bookingId, searchParams, user, navigate]);

  if (status === 'loading') {
    return (
      <div className="payment-container">
        <div className="payment-card loading">
          <Loader size={48} className="spinner" />
          <p>Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="payment-container">
        <div className="payment-card success">
          <Check size={64} className="icon" />
          <h1>Payment Successful!</h1>
          <p>Your booking has been confirmed</p>
          <div className="booking-details">
            <div className="detail-row">
              <span>Booking ID:</span>
              <span>{booking?.bookingId}</span>
            </div>
            <div className="detail-row">
              <span>Amount Paid:</span>
              <span>${booking?.totalPrice}</span>
            </div>
            <div className="detail-row">
              <span>Status:</span>
              <span className="status-badge confirmed">Confirmed</span>
            </div>
          </div>
          <p className="confirmation-msg">
            A confirmation email has been sent to your registered email address.
          </p>
          <div className="actions">
            <button onClick={() => navigate('/tours')} className="btn-primary">
              View More Tours
            </button>
            <button onClick={() => navigate('/')} className="btn-secondary">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="payment-container">
        <div className="payment-card pending">
          <Loader size={64} className="spinner" />
          <h1>Payment Pending</h1>
          <p>Your payment is being processed</p>
          <p className="pending-msg">
            This usually takes a few moments. Please don't refresh the page.
          </p>
          <div className="actions">
            <button onClick={() => window.location.reload()} className="btn-primary">
              Check Status
            </button>
            <button onClick={() => navigate('/tours')} className="btn-secondary">
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-container">
      <div className="payment-card failed">
        <X size={64} className="icon" />
        <h1>Payment Failed</h1>
        <p>{error || 'There was an issue processing your payment'}</p>
        <div className="actions">
          <button onClick={() => navigate(`/tour/${booking?.bookingId}`)} className="btn-primary">
            Try Again
          </button>
          <button onClick={() => navigate('/tours')} className="btn-secondary">
            Back to Tours
          </button>
        </div>
      </div>
    </div>
  );
}
